/**
 * The features a member can be gated out of, and the one answer both apps read for each.
 *
 * A locked feature in hive and the row that lights up on checkout's comparison table are the
 * same claim about the same plan, so they resolve from one entry rather than two copies that
 * drift: `requiredPlan` is what the member has to be on, `rowId` is the comparison row that
 * proves it (checkout's `ComparisonFeature.id` AND hive's benefit-row id), and `key` is the
 * only thing that travels on a link between the apps.
 *
 * Only the features whose plan mapping is decided live here. The rest stay out until product
 * answers which tier carries them — an entry invented to fill the table is a promise the
 * checkout page would then make on the pricing screen.
 */
export type PlanSlug = 'essentials' | 'pro' | 'signature';

export interface FeatureGate {
    /** URL-safe; the ONLY thing that travels on a link. */
    key: string;
    /** "Search Presence"; rendered into titles, chips, footer link. */
    label: string;
    requiredPlan: PlanSlug;
    /** Matches checkout's ComparisonFeature.id AND hive's benefit row id. */
    rowId: string;
    /** Where "Open {label}" goes, and the default return path. */
    hivePath: string;
}

export const FEATURE_GATES: Record<string, FeatureGate> = {
    'search-presence': {
        key: 'search-presence',
        label: 'Search Presence',
        requiredPlan: 'pro',
        rowId: 'search-engine',
        hivePath: '/search-presence',
    },
    // The dark web exposure scan. Both catalogues already sell it from Pro up under this row, and
    // the API refuses it below that (`breach_monitoring`), so the mapping is decided.
    'breach-monitoring': {
        key: 'breach-monitoring',
        label: 'Breach Monitoring',
        requiredPlan: 'pro',
        rowId: 'breach',
        hivePath: '/dark-web-scan',
    },
    'threat-hunting': {
        key: 'threat-hunting',
        label: 'Threat Hunting',
        requiredPlan: 'signature',
        rowId: 'threat-hunting',
        hivePath: '/dark-web',
    },
    'deep-scan': {
        key: 'deep-scan',
        label: 'Deep Dark Web Scan',
        requiredPlan: 'signature',
        rowId: 'dark-web',
        hivePath: '/dark-web',
    },
};

/**
 * The gate a link's `feature` parameter names, or null when it names nothing we publish.
 * The input is whatever came off a query string, so an array or a missing value is a normal
 * case and not an error — both answer null, and the caller falls back to the plain page.
 */
export function resolveFeatureGate(key: unknown): FeatureGate | null {
    if (typeof key !== 'string') return null;
    // Query strings can name inherited properties; only published own keys represent a gate.
    if (!Object.hasOwn(FEATURE_GATES, key)) return null;
    return FEATURE_GATES[key] ?? null;
}
